/* eslint-disable react/prop-types */
/**
 * CountySelect — a County dropdown that cascades off the sibling State field.
 *
 * The venue grids (Lease/Deeds Recordings & Tracts, Well Tracts, Assignment
 * Recordings) render State + County as metadata `select` fields. County is far
 * too long to be one flat list, so this control scopes the options to the State
 * chosen in the same row: it reads `state` from the form via `Form.useWatch`.
 *
 * TWO OPTION SOURCES, IN PRIORITY ORDER
 * -------------------------------------
 * 1. `row.options` from GET /metadata — the PREFERRED source. de-hub migration
 *    068 seeds `county` into `pick_lists` under the shared table_name with every
 *    row scoped to its state (`parent_value` = USPS code), covering all 50
 *    states + DC (3,143 county-equivalents). The metadata payload carries those
 *    options inline plus `options_parent_field` naming the field that scopes
 *    them, so this control just filters in memory — no request, no per-state
 *    coverage gaps, and the same vocabulary the API validates against.
 *
 * 2. The spatial service (`counties_<state>.geojson` fixtures) — FALLBACK, used
 *    only when the metadata carries no options for the field. That is the case
 *    against a database where migration 068 has not been applied yet, where
 *    only OK + TX fixtures exist. Keeping this path means deploying the app
 *    ahead of the migration degrades to the old behaviour instead of emptying
 *    the dropdown entirely.
 *
 * Source 1 is why a state like New Mexico used to show "No counties for this
 * state": it has no geojson fixture, and the fixtures were the only source.
 *
 * Wired via WigMetadataForm/WigMetadataCrudPanel `customRenderers.county`
 * (see `countyFieldRenderer`), which renders inside the panel's edit Form — so
 * `Form.useFormInstance()` / `Form.useWatch` resolve the active row's form, and
 * the wrapping `Form.Item` injects `value`/`onChange`.
 */
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Form, Select } from "antd";

import { AppContext } from "../AppContextProvider";
import spatialService from "../services/spatialService";

// Per-state option cache (module-level) so re-opening rows/panels doesn't refetch.
const _countyCache = {};

// The spatial endpoints return a FeatureCollection, sometimes wrapped in the
// standard { data: ... } envelope. Mirror useSpatialData's unwrap.
const extractFeatures = (resp) => {
  const body = resp?.data;
  if (body?.type === "FeatureCollection") return body.features || [];
  if (body?.data?.type === "FeatureCollection") return body.data.features || [];
  if (Array.isArray(body?.data?.features)) return body.data.features;
  if (Array.isArray(body?.features)) return body.features;
  return [];
};

const countyNameOf = (f) => {
  const p = f?.properties || {};
  return String(p.county ?? p.name ?? p.NAME ?? p.COUNTY ?? "").trim().toUpperCase();
};

export const CountySelect = ({ value, onChange, disabled, row }) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const form = Form.useFormInstance();
  // The scoping field is named by the metadata; `state` is the venue default.
  const parentField = row?.options_parent_field || "state";
  const stateVal = Form.useWatch(parentField, form);
  const st = (stateVal || "").toString().trim().toUpperCase();

  // Source 1: options inlined by GET /metadata, scoped to the chosen state.
  // Unscoped options (no parent_value) always show — they apply to every state.
  const metaOptions = useMemo(() => {
    const all = Array.isArray(row?.options) ? row.options : [];
    if (!all.length) return null; // signals "fall back to the spatial fetch"
    if (!st) return [];
    return all
      .map((o) => (typeof o === "string" ? { value: o, label: o } : o))
      .filter((o) => {
        const pv = o?.parent_value;
        if (pv == null || pv === "") return true;
        return String(pv).trim().toUpperCase() === st;
      })
      .map((o) => ({ value: o.value, label: o.label ?? o.value }));
  }, [row, st]);

  const [fetched, setFetched] = useState([]);
  const [loading, setLoading] = useState(false);

  // Source 2 (fallback): load counties from the spatial service. Skipped
  // entirely when the metadata supplied options, so the common path makes no
  // request at all.
  useEffect(() => {
    let cancelled = false;
    if (metaOptions !== null) {
      setLoading(false);
      return undefined;
    }
    if (!st) {
      setFetched([]);
      return undefined;
    }
    if (_countyCache[st]) {
      setFetched(_countyCache[st]);
      return undefined;
    }
    (async () => {
      setLoading(true);
      try {
        const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
        let tk = token;
        if (!tk || isTokenExpired(tk)) tk = await getToken();
        const feats = extractFeatures(
          await spatialService.getCounties(tk, userOid, st)
        );
        const names = [...new Set(feats.map(countyNameOf).filter(Boolean))].sort();
        const opts = names.map((n) => ({ value: n, label: n }));
        _countyCache[st] = opts;
        if (!cancelled) setFetched(opts);
      } catch {
        if (!cancelled) setFetched([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [metaOptions, st, token, getToken, isTokenExpired, user]);

  const options = metaOptions !== null ? metaOptions : fetched;

  // Clear the county ONLY when the user changes the state to a different value
  // (not on initial edit-load, which would wipe a valid saved county).
  const prevStateRef = useRef(st);
  useEffect(() => {
    const prev = prevStateRef.current;
    if (prev && prev !== st && value) onChange?.(undefined);
    prevStateRef.current = st;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st]);

  return (
    <Select
      value={value ?? undefined}
      onChange={onChange}
      options={options}
      loading={loading}
      disabled={disabled}
      allowClear
      showSearch
      optionFilterProp="label"
      placeholder={st ? "Select a county" : "Select a state first"}
      notFoundContent={
        !st ? "Select a state first" : loading ? "Loading…" : "No counties for this state"
      }
    />
  );
};

// customRenderers.county factory — renders its own Form.Item per the
// WigMetadataForm customRenderers contract ("consumer renders their own
// Form.Item"), wrapping the cascading CountySelect.
//
// `row` is forwarded so the control can read the inlined `options` /
// `options_parent_field` from the metadata rather than fetching.
export const countyFieldRenderer = (row) => (
  <Form.Item
    key={row?.field || "county"}
    name={row?.field || "county"}
    label={row?.label || "County"}
    rules={row?.required ? [{ required: true, message: "County is required" }] : undefined}
  >
    <CountySelect row={row} />
  </Form.Item>
);

export default CountySelect;
